import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import type { NotificationView } from '@lims/shared-types';
import { notificationsApi } from '../../api/endpoints.js';
import { formatDateTime } from '../../components/format.js';
import { POPOVER_MOTION_CLASS } from '../../components/ui/popover-motion.js';
import { currentLocale } from '../../i18n/index.js';
import { notificationTarget } from '../quality/operations-ui.js';
import { useNotificationText } from './NotificationsPages.js';

/** The newest few; the full page holds the rest. */
const PANEL_SIZE = 8;

/**
 * The bell in the top bar, and the panel that drops from it (D278).
 *
 * The approved redesign replaced the notifications link in the old rail with
 * the pattern people know from every large platform: a bell with an unread
 * count, a short panel of the newest notifications under it, and a link to
 * the full page. Opening a notification marks it read and goes straight to
 * its work item, exactly as the full page does; that page re-checks the
 * caller's permission like every page.
 *
 * Every signed-in person has notifications; there is no permission to check.
 * A count that could not be read is simply not shown — never shown as zero.
 */
export function NotificationsBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const text = useNotificationText();
  const [isOpen, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 60_000,
  });
  const list = useQuery({
    queryKey: ['notifications', 'panel', { unreadOnly }],
    queryFn: () =>
      notificationsApi.list({ page: 1, pageSize: PANEL_SIZE, ...(unreadOnly ? { unreadOnly: 'true' } : {}) }),
    enabled: isOpen,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });
  const readAll = useMutation({ mutationFn: () => notificationsApi.markAllRead(), onSuccess: refresh });

  const count = unread.data?.unread;
  const hasUnread = typeof count === 'number' && count > 0;

  const open = async (item: NotificationView) => {
    if (!item.readAt) {
      await notificationsApi.markRead(item.id);
      await refresh();
    }
    setOpen(false);
    const target = notificationTarget(item);
    if (target) navigate(target);
  };

  const rows = list.data?.data ?? [];

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setOpen}
      placement="bottom-end"
      offset={6}
      disableAnimation
      classNames={{ content: `${POPOVER_MOTION_CLASS} p-0` }}
    >
      <PopoverTrigger>
        <button
          type="button"
          className="relative grid size-9 shrink-0 place-items-center rounded-medium text-default-700 transition-colors hover:bg-default-100 hover:text-default-900"
          aria-label={
            hasUnread
              ? `${t('nav.notifications')} — ${t('notifications.unreadCount', { value: count })}`
              : t('nav.notifications')
          }
        >
          <BellIcon />
          {hasUnread ? (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 end-0 min-w-[18px] rounded-full bg-danger-600 px-1 text-center text-[11px] font-semibold leading-[18px] text-white ring-2 ring-white"
            >
              {count > 99 ? '99+' : count}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="flex max-h-[min(540px,calc(100vh-90px))] w-[min(380px,calc(100vw-24px))] flex-col text-default-900">
          <div className="flex items-center gap-2 px-4 pb-1.5 pt-3">
            <h2 className="flex-1 text-section font-bold">{t('notifications.title')}</h2>
            {hasUnread ? (
              <button
                type="button"
                className="rounded-small px-1.5 py-1 text-caption font-medium text-primary-700 hover:bg-primary-50"
                onClick={() => readAll.mutate()}
                disabled={readAll.isPending}
              >
                {t('notifications.markAllRead')}
              </button>
            ) : null}
          </div>
          <div role="tablist" aria-label={t('notifications.title')} className="flex gap-1 border-b border-default-200 px-3 pb-2">
            {[false, true].map((only) => (
              <button
                key={String(only)}
                type="button"
                role="tab"
                aria-selected={unreadOnly === only}
                onClick={() => setUnreadOnly(only)}
                className={`rounded-full px-3 py-1 text-caption font-medium transition-colors ${
                  unreadOnly === only ? 'bg-primary-100 text-primary-800' : 'text-default-700 hover:bg-default-100'
                }`}
              >
                {only ? t('notifications.unread') : t('notifications.all')}
                {only && hasUnread ? <span className="ms-1 text-default-600">{count}</span> : null}
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {list.isLoading ? (
              <p className="px-3 py-6 text-center text-caption text-default-600">{t('common.loading')}</p>
            ) : list.error ? (
              <p className="px-3 py-6 text-center text-caption text-danger-700">{t('notifications.loadFailed')}</p>
            ) : rows.length === 0 ? (
              <p className="px-3 py-6 text-center text-caption text-default-600">
                {unreadOnly ? t('notifications.allCaughtUp') : t('notifications.none')}
              </p>
            ) : (
              <ul className="flex flex-col">
                {rows.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => void open(item)}
                      className={`flex w-full items-start gap-2.5 rounded-medium px-2.5 py-2 text-start transition-colors hover:bg-default-100 ${
                        item.readAt ? '' : 'bg-primary-50/70'
                      }`}
                    >
                      <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full border border-default-200 bg-default-50 text-default-700">
                        <BellIcon size={15} />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className={`text-caption ${item.readAt ? 'text-default-800' : 'font-semibold text-default-900'}`}>
                          {text(item)}
                        </span>
                        <span className={`text-micro ${item.readAt ? 'text-default-600' : 'font-semibold text-primary-700'}`}>
                          {formatDateTime(item.createdAt, { locale })}
                        </span>
                      </span>
                      {item.readAt ? null : (
                        <span className="mt-2 size-2 shrink-0 rounded-full bg-primary-600">
                          <span className="sr-only">{t('notifications.unread')}</span>
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-default-200 px-4 py-2.5 text-center text-caption font-semibold text-primary-700 no-underline hover:bg-default-50"
          >
            {t('notifications.seeAll')}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BellIcon({ size = 18 }: { readonly size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4z" />
      <path d="M10 21h4" />
    </svg>
  );
}
